import type { IVAConfiguration } from './types';

type TimerHandle = ReturnType<typeof setTimeout>;

export interface WorkingFillerScheduler {
  now: () => number;
  setTimeout: (callback: () => void, delayMs: number) => TimerHandle;
  clearTimeout: (handle: TimerHandle) => void;
}

export interface WorkingFillerController {
  begin: (operationKind: string) => () => void;
  startTurn: () => void;
  endTurn: () => void;
  audioStarted: () => void;
  audioEnded: () => void;
  dispose: () => void;
}

const DEFAULT_SCHEDULER: WorkingFillerScheduler = {
  now: () => Date.now(),
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: handle => clearTimeout(handle),
};

const PHRASES: Record<string, string> = {
  en: 'One moment, please.',
  pt: 'Um momento, por favor.',
  es: 'Un momento, por favor.',
};

export function resolveWorkingFillerPhrase(config: IVAConfiguration): string {
  const language = (config.primaryLanguage || (config as any).language || 'en').toLowerCase().split(/[-_]/)[0];
  return PHRASES[language] || PHRASES.en;
}

export function resolveWorkingDelayPhrase(config: IVAConfiguration): string {
  const language = (config.primaryLanguage || 'en').toLowerCase().split(/[-_]/)[0];
  return language === 'pt' ? 'A consulta está demorando um pouco mais. Ainda estou verificando.'
    : language === 'es' ? 'La consulta está tardando un poco más. Sigo verificando.'
      : 'This is taking a little longer. I am still checking.';
}

export function buildWorkingFillerInstructions(config: IVAConfiguration, phrase: string): string {
  const guidance = config.aiSettings?.fillerCustomInstruction?.trim();
  return `Give only one brief waiting acknowledgement in ${config.primaryLanguage || 'en-US'}, under six seconds. ` +
    'Do not call tools, ask a question, or claim that information was saved or an action completed. ' +
    `Neutral fallback wording: ${JSON.stringify(phrase)}. ` +
    (guidance ? `Adapt the wording and tone to this configured guidance: ${guidance}` : 'Use the neutral fallback wording.');
}

export function createWorkingFillerController(
  config: IVAConfiguration,
  callbacks: {
    canSpeak: () => boolean;
    speak: (phrase: string, operationKind: string) => void | boolean;
    stop?: (operationKind: string) => void;
    log?: (message: string) => void;
  },
  scheduler: WorkingFillerScheduler = DEFAULT_SCHEDULER,
  options: { enabled?: boolean } = {},
): WorkingFillerController {
  const enabled = options.enabled ?? config.aiSettings?.fillerMode === 'vocal_filler';
  const delayMs = config.aiSettings?.fillerDelayMs ?? 1000;
  const cooldownMs = config.aiSettings?.fillerCooldownMs ?? 8000;
  const phrase = resolveWorkingFillerPhrase(config);
  const operations = new Map<object, string>();
  const delayedPhrase = resolveWorkingDelayPhrase(config);
  let timer: TimerHandle | undefined;
  let disposed = false;
  let lastSpokenAt = Number.NEGATIVE_INFINITY;
  let lastAudioEndedAt = Number.NEGATIVE_INFINITY;
  let lastFillerEndedAt = Number.NEGATIVE_INFINITY;
  let audioActive = false;
  let fillerAudioExpected = false;
  let lastAudioWasFiller = false;
  let turnActive = false;
  let workStartedAt = 0;
  let spokenOperation: string | undefined;

  const pending = () => turnActive || operations.size > 0;
  const clearTimer = () => {
    if (timer !== undefined) scheduler.clearTimeout(timer);
    timer = undefined;
  };
  const schedule = (waitMs: number) => {
    clearTimer();
    if (!enabled || disposed || !pending()) return;
    timer = scheduler.setTimeout(attempt, Math.max(100, waitMs));
  };
  const attempt = () => {
    timer = undefined;
    if (disposed || !pending()) return;
    const now = scheduler.now();
    // The interval is measured from the end of the previous waiting message, even when other
    // agent audio (e.g. a partial reply) played in between.
    const eligibleAt = Math.max(workStartedAt + delayMs, lastSpokenAt + cooldownMs,
      lastFillerEndedAt + cooldownMs, lastAudioEndedAt + (lastAudioWasFiller ? cooldownMs : delayMs));
    if (now < eligibleAt) { schedule(eligibleAt - now); return; }
    if (audioActive || !callbacks.canSpeak()) { schedule(100); return; }
    const operationKind = operations.values().next().value || 'turn';
    if (callbacks.speak(now - workStartedAt >= 20000 ? delayedPhrase : phrase, operationKind) === false) {
      schedule(100);
      return;
    }
    lastSpokenAt = now;
    fillerAudioExpected = true;
    spokenOperation = operationKind;
    const sinceFillerEnd = Number.isFinite(lastFillerEndedAt) ? `${now - lastFillerEndedAt}` : 'none';
    callbacks.log?.(`working filler fired operation=${operationKind} pending=${operations.size} elapsedMs=${now - workStartedAt}`
      + ` sinceLastFillerEndMs=${sinceFillerEnd}`);
    schedule(cooldownMs);
  };
  const stop = () => {
    clearTimer();
    if (spokenOperation) callbacks.stop?.(spokenOperation);
    spokenOperation = undefined;
  };

  return {
    begin(operationKind: string): () => void {
      if (!enabled || disposed) return () => {};
      const wasPending = pending();
      const operation = {};
      operations.set(operation, operationKind);
      if (!wasPending) {
        workStartedAt = scheduler.now();
      }
      schedule(wasPending ? 100 : delayMs);
      return () => {
        operations.delete(operation);
        if (!pending()) stop();
      };
    },
    startTurn(): void {
      if (!enabled || disposed || turnActive) return;
      const wasPending = pending();
      turnActive = true;
      if (!wasPending) {
        workStartedAt = scheduler.now();
        schedule(delayMs);
      }
    },
    endTurn(): void {
      turnActive = false;
      operations.clear();
      stop();
    },
    audioStarted(): void {
      if (!audioActive) {
        lastAudioWasFiller = fillerAudioExpected;
        fillerAudioExpected = false;
      }
      audioActive = true;
    },
    audioEnded(): void {
      if (!audioActive) return;
      audioActive = false;
      lastAudioEndedAt = scheduler.now();
      if (lastAudioWasFiller) {
        lastFillerEndedAt = lastAudioEndedAt;
        callbacks.log?.('working filler audio ended');
      }
      // The interval separates waiting messages; after the agent's own speech the normal delay applies.
      schedule(lastAudioWasFiller ? cooldownMs : delayMs);
    },
    dispose(): void {
      disposed = true;
      operations.clear();
      turnActive = false;
      stop();
    },
  };
}